// eslint-disable-next-line @nx/enforce-module-boundaries
import type { TrackingPropsInterface } from '@marriott/mi-ui-library';
import { SlotContent } from '../../organisms/DynamicTabbedLayout/DynamicTabbedLayout.types';

export interface LayeredFeatureImage {
  image?: {
    damPath?: string;
    assetPath?: string;
    altText?: string;
    dynamic?: boolean;
    enableSmartCrop?: boolean;
  };
}

export interface LayeredFeatureTrackingProperties {
  [key: string]: string;
}

/** Mirrors imageUrls from dcaDACImageFragment */
export interface DcaImageUrls {
  panoramicHorizontal?: string;
  panoramicVertical?: string;
  wideHorizontal?: string;
  wideVertical?: string;
  featureHorizontal?: string;
  featureVertical?: string;
  classicHorizontal?: string;
  classicVertical?: string;
  standardVertical?: string;
  standardHorizontal?: string;
  square?: string;
  deviceSmall?: string;
  flex?: string;
}

/** Mirrors DCADacImage fragment - image object in mbox adData */
export interface DcaImage {
  imageUrls?: DcaImageUrls;
  imageSrc?: string;
  alternateDescription?: string;
  assetPath?: string;
  defaultImage?: string;
  roomTypeCodes?: string;
  title?: string;
  caption?: string;
  marketingCaption?: string;
}

/** Mbox item shape from adData - mirrors mboxes fields in dcaDynamicContentV2.graphql */
export interface DcaContentPayload {
  eyebrowText?: string;
  headerLong?: string;
  headerMedium?: string;
  headerShort?: string;
  descriptionLong?: string;
  descriptionMedium?: string;
  descriptionShort?: string;
  callToActionText1?: string;
  callToActionText2?: string;
  destinationUrl1?: string;
  destinationUrl2?: string;
  image?: DcaImage;
  imageAltText?: string;
  openInNewTab?: boolean;
  mathLabel1?: string | null;
  mathLabel2?: string | null;
  mathLabel3?: string | null;
  mathValue?: string | null;
  mathType?: string | null;
  mathTitle?: string | null;
  legalLong?: string;
  legalShort?: string;
  caption?: string;
  captionUrl?: string;
  imageAssetId?: string;
  logoAssetId?: string;
  mboxName?: string;
  metadata?: unknown;
  promotionalTag?: string;
}

/** Content fragment (AEM) shape for layered feature cards - from AEM GraphQL / normalizeCfItems */
export interface LayeredFeatureCfContent {
  eyebrow?: string;
  header?: string;
  title?: string;
  description?: string | { plaintext?: string };
  assetPath?: string;
  imageAltText?: string;
  featureImage?: LayeredFeatureImage;
  desktopVariation?: string;
  tabletVariation?: string;
  mobileVariation?: string;
  primaryCtaText?: string;
  primaryCtaLink?: string;
  primaryCtaOpenInNewTab?: boolean;
  primaryCtaAriaLabel?: string;
  secondaryCtaText?: string;
  secondaryCtaLink?: string;
  secondaryCtaOpenInNewTab?: boolean;
  secondaryCtaAriaLabel?: string;
  tertiaryLinkText?: string;
  tertiaryLinkUrl?: string;
  tertiaryLinkOpenInNewTab?: boolean;
  tertiaryLinkAriaLabel?: string;
  styleclass?: string;
  styleClass?: string;
  trackingProperties?: LayeredFeatureTrackingProperties;
  componentId?: string;
  brandLogoIcon?: string;
  custom_click_track_value_primary?: string;
  callToActionText1?: string;
  callToActionText2?: string;
  destinationUrl1?: string;
  destinationUrl2?: string;
  openInNewTab?: boolean;
}

/** Metadata shape from DCA response */
export interface DcaMetadata {
  campaignId?: string;
  cell?: string;
  countryCode?: string;
  endDate?: string;
  issuerCode?: string;
  loyaltyBar?: boolean;
  mathOffer?: boolean;
  offerCode?: string;
  rpcCode?: string;
  spid?: string;
  startDate?: string;
  theme?: string;
  title?: string;
  type?: string;
}

/** DCA-centric props for MerchandisingLayeredFeature (matches component contract) */
export interface LayeredFeatureDcaProps {
  componentId?: string;
  logoImage?: string;
  eyebrowText?: string;
  headerLong?: string;
  headerMedium?: string;
  headerShort?: string;
  descriptionLong?: string;
  descriptionMedium?: string;
  descriptionShort?: string;
  callToActionText1?: string;
  destinationUrl1?: string;
  callToActionText2?: string;
  destinationUrl2?: string;
  openInNewTab?: boolean;
  tertiaryLinkText?: string;
  tertiaryLinkUrl?: string;
  tertiaryLinkOpenInNewTab?: boolean;
  backgroundImage: LayeredFeatureImage;
  desktopVariation?: string;
  tabletVariation?: string;
  mobileVariation?: string;
  className?: string;
  trackingProperties?: TrackingPropsInterface;
  custom_click_track_value_primary?: string;
  mathType?: string;
  mathLabel1?: string;
  mathLabel2?: string;
  mathLabel3?: string;
  mathValue?: string;
  mathTitle?: string;
  legalLong?: string;
  metadata?: DcaMetadata;
  mboxParameter?: string;
}

export type LayeredFeatureContentPayload = DcaContentPayload | LayeredFeatureCfContent;

export type LayeredFeatureDCAPayload = {
  slot: SlotContent;
  contentObj: DcaContentPayload;
  baseUrl: string;
  alt: string;
};

export type LayeredFeatureCfPayload = {
  slot: SlotContent;
  contentObj: LayeredFeatureCfContent;
  baseUrl: string;
  alt: string;
};

export type LayeredFeatureAdapterCtx = LayeredFeatureCfPayload | LayeredFeatureDCAPayload;

export function isCfContent(
  ctx: LayeredFeatureAdapterCtx
): ctx is LayeredFeatureAdapterCtx & { contentObj: LayeredFeatureCfContent } {
  return ctx.slot?.type === 'cf';
}

export function isDcaContent(
  ctx: LayeredFeatureAdapterCtx
): ctx is LayeredFeatureAdapterCtx & { contentObj: DcaContentPayload } {
  return ctx.slot?.type === 'dca';
}

export function isCoBrandDca(ctx: LayeredFeatureAdapterCtx): boolean {
  if (!isDcaContent(ctx)) return false;
  const metadata = ctx.contentObj.metadata as DcaMetadata | undefined;
  return metadata?.type?.toLowerCase() === 'cobrand';
}
